import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";

/**
 * Badge Component
 *
 * A small label used to highlight status, categories, or counts.
 * Supports optional left/right icons, two color themes, and two sizes.
 */

// Define variant styles using class-variance-authority
const badgeVariants = cva(
  `flex items-center overflow-hidden rounded-full p-0.5`,
  {
    variants: {
      color: {
        primary:
          "text-[var(--text-base-primary)] bg-[var(--fill-primary-tonal-normal)]",
        neutral: "text-[var(--text-gray-normal)] bg-[var(--fill-base-gray)]",
      },
      size: {
        md: "text-xs",
        lg: "text-sm",
      },
    },
    defaultVariants: {
      color: "primary",
      size: "md",
    },
  },
);

// Main badge wrapper — flexible to support icons and text
const Badge = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    className?: string;
    rightIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
    leftIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
    leftIconClass?: string;
    color?: "primary" | "neutral";
    size?: "md" | "lg";
    wrapperClassName?: string;
  }
>(
  (
    {
      className,
      leftIcon: LeftIconComponent,
      leftIconClass,
      rightIcon: RightIconComponent,
      color,
      size = "md",
      wrapperClassName,
      ...props
    },
    ref,
  ) => {
    let leftIconElement: React.ReactNode = null;

    if (LeftIconComponent) {
      leftIconElement = (
        <LeftIconComponent
          className={cn("flex-shrink-0", {
            "h-4 w-4 text-sm": size === "md",
            "h-[18px] w-[18px] text-lg": size === "lg",
          })}
          fill="currentColor"
        />
      );
    } else if (leftIconClass) {
      leftIconElement = (
        <i
          className={cn(
            leftIconClass,
            "flex-shrink-0",
            size === "md" ? "h-4 w-4 text-sm" : "h-[18px] w-[18px] text-lg",
          )}
        />
      );
    }
    return (
      <div
        ref={ref}
        className={cn(badgeVariants({ color, size }), className)}
        {...props}
      >
        {leftIconElement}
        <span
          className={`truncate px-1 ${wrapperClassName && wrapperClassName}`}
        >
          {props.children}
        </span>
        {RightIconComponent && (
          <RightIconComponent
            className={cn("flex-shrink-0", {
              "h-4 w-4 text-sm": size === "md",
              "h-[18px] w-[18px] text-lg": size === "lg",
            })}
            fill="currentColor"
          />
        )}
      </div>
    );
  },
);

Badge.displayName = "Badge";

export { Badge };
